import { ButtonGroup } from "@rumble-it/common-components/components";
import { Button } from "@/components/common/button";
import { Table, Popconfirm, message, Input, Select } from "antd";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  fetchAgentsList,
  fetchAiProfiles,
  deleteAgent,
  AgentListItem,
  AiProfile,
} from "@/lib/api/resources/agents";
import { Link, useNavigate } from "react-router-dom";
import {
  EditOutlined,
  DeleteOutlined,
  ExclamationCircleFilled,
  SearchOutlined,
} from "@ant-design/icons";
import type { SorterResult } from "antd/es/table/interface";

const GRADIENTS = [
  "from-blue-500 to-indigo-600 shadow-blue-500/20",
  "from-emerald-400 to-teal-500 shadow-emerald-500/20",
  "from-rose-400 to-red-500 shadow-rose-500/20",
  "from-amber-400 to-orange-500 shadow-amber-500/20",
  "from-violet-500 to-purple-600 shadow-violet-500/20",
  "from-pink-500 to-rose-600 shadow-pink-500/20",
  "from-cyan-500 to-blue-600 shadow-cyan-500/20",
];

const getAvatarStyle = (name: string) => {
  if (!name) return GRADIENTS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % GRADIENTS.length;
  return GRADIENTS[index];
};

const getInitials = (name: string) => {
  if (!name) return "R";
  const parts = name.split(/[-_\s]+/);
  if (parts.length >= 2 && parts[0].length > 0 && parts[1].length > 0) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export default function AgentsList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState("");
  const [order, setOrder] = useState<{
    field: "name" | "profileName";
    direction: "asc" | "desc";
  }>({ field: "name", direction: "asc" });
  const [aiProfileId, setAiProfileId] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);

  const pageSize = 15;

  const { data: agentsData, isLoading } = useQuery({
    queryKey: ["agents", { search, order, aiProfileId, page }],
    queryFn: () =>
      fetchAgentsList({
        name: search || undefined,
        orderField: order?.field,
        orderDirection: order?.direction,
        aiProfileId,
        page,
      }),
  });

  const { data: aiProfilesData } = useQuery({
    queryKey: ["ai-profiles"],
    queryFn: fetchAiProfiles,
  });

  const profileOptions = (
    Array.isArray(aiProfilesData)
      ? aiProfilesData
      : (aiProfilesData as any)?.member ||
        (aiProfilesData as any)?.["hydra:member"] ||
        []
  ).map((profile: AiProfile) => ({
    value: profile.id,
    label: profile.name,
  }));

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteAgent(id),
    onSuccess: () => {
      message.success("Agent deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["agents"] });
    },
    onError: () => {
      message.error("Failed to delete agent");
    },
  });

  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    deleteMutation.mutate(id);
  };

  const dataSource = Array.isArray(agentsData)
    ? agentsData
    : (agentsData as any)?.member ||
      (agentsData as any)?.["hydra:member"] ||
      [];

  const totalItems = Array.isArray(agentsData)
    ? agentsData.length
    : (agentsData as any)?.totalItems;

  const sortOrderFor = (field: "name" | "profileName") =>
    order?.field === field
      ? order.direction === "asc"
        ? ("ascend" as const)
        : ("descend" as const)
      : null;

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      sortOrder: sortOrderFor("name"),
      render: (text: string, record: AgentListItem) => {
        const displayName = text || record.name || "Unknown";
        const initials = getInitials(displayName);
        const idForHash =
          record.id || (record as any)["@id"] || record.name || "agent";
        const gradientClass = getAvatarStyle(idForHash);

        return (
          <div className="flex items-center gap-3 py-2">
            <div
              className={`h-10 w-10 rounded-xl bg-gradient-to-tr ${gradientClass} flex items-center justify-center shadow-md text-white shrink-0 font-bold tracking-wider`}
            >
              {initials}
            </div>
            <span className="font-semibold text-gray-800 text-sm tracking-tight">
              {displayName}
            </span>
          </div>
        );
      },
    },
    {
      title: "AI Profile",
      key: "profileName",
      sorter: true,
      sortOrder: sortOrderFor("profileName"),
      render: (_: any, record: AgentListItem) => {
        const providerModel = [record.providerLabel, record.model]
          .filter(Boolean)
          .join(" / ");

        return (
          <div className="flex flex-col">
            <span className="text-sm font-medium text-slate-700">
              {record.profileName || (
                <span className="text-slate-400 italic">None</span>
              )}
            </span>
            {providerModel ? (
              <span className="text-xs text-slate-400">({providerModel})</span>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "",
      key: "actions",
      align: "right" as const,
      width: 150,
      render: (_: any, record: AgentListItem) => {
        const id = record.id || (record as any)["@id"]?.split("/").pop();
        return (
          <div className="flex items-center justify-end gap-1 opacity-100 lg:opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-200">
            <Link
              to={`/agents/${id}/edit`}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-3 text-gray-600 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 shadow-sm transition-all rounded-lg"
              >
                <EditOutlined className="mr-1.5" /> Edit
              </Button>
            </Link>
            <Popconfirm
              title="Delete Agent"
              icon={<ExclamationCircleFilled className="text-red-500" />}
              description={
                <div className="max-w-[250px] mt-1 text-gray-500">
                  Are you sure you want to permanently delete this agent? This
                  action cannot be undone.
                </div>
              }
              onConfirm={(e) => handleDelete(e as any, id as string)}
              onCancel={(e) => e?.stopPropagation()}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{
                loading: deleteMutation.isPending,
                className:
                  "bg-red-600 hover:bg-red-700 text-white border-none shadow-md shadow-red-500/20",
              }}
            >
              <Button
                variant="outline"
                size="sm"
                className="h-8 w-8 p-0 text-gray-400 hover:text-red-600 hover:border-red-200 hover:bg-red-50 shadow-sm transition-all rounded-lg flex items-center justify-center"
                onClick={(e) => e.stopPropagation()}
              >
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <ButtonGroup className="flex mb-4 gap-3 items-center">
        <Input
          allowClear
          prefix={<SearchOutlined className="text-slate-400" />}
          placeholder="Search agents by name"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="max-w-xs"
        />
        <Select
          allowClear
          showSearch
          placeholder="Filter by AI Profile"
          value={aiProfileId}
          onChange={(value) => {
            setAiProfileId(value);
            setPage(1);
          }}
          options={profileOptions}
          optionFilterProp="label"
          className="min-w-[200px]"
        />
        <Button
          className="ml-auto shadow-sm shadow-blue-500/20"
          variant="primary"
          onClick={() => navigate("/agents/create")}
        >
          Create New Agent
        </Button>
      </ButtonGroup>

      <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-white">
        <Table
          dataSource={dataSource}
          columns={columns}
          rowKey={(record) => record.id || (record as any)["@id"]}
          loading={isLoading}
          onChange={(_pagination, _filters, sorter, extra) => {
            if (extra.action !== "sort") {
              return;
            }
            const { columnKey, order: sortOrder } =
              sorter as SorterResult<AgentListItem>;
            if (
              sortOrder &&
              (columnKey === "name" || columnKey === "profileName")
            ) {
              setOrder({
                field: columnKey,
                direction: sortOrder === "ascend" ? "asc" : "desc",
              });
            } else {
              // Sorting cleared: fall back to the default name ASC order.
              setOrder({ field: "name", direction: "asc" });
            }
            setPage(1);
          }}
          pagination={{
            current: page,
            pageSize,
            total: totalItems,
            showSizeChanger: false,
            onChange: (nextPage) => setPage(nextPage),
            className: "px-4 pb-4 mb-0",
          }}
          onRow={(record) => {
            const id = record.id || (record as any)["@id"]?.split("/").pop();
            return {
              onClick: () => navigate(`/agents/${id}/edit`),
              className:
                "group hover:bg-slate-50/50 transition-colors duration-200 cursor-pointer",
            };
          }}
        />
      </div>
    </div>
  );
}
